import { alert } from 'actions/flash'
import { landingPageThemeApi } from 'api/landingPageTheme'
import { selectNavigationStyleIsSubmitting } from 'api/navigationStyle'
import { selectNavigationTabsIsSubmitting } from 'api/navigationTabs'
import { TabT } from 'components/Tabs/InnerTabs'
import { useSubmitBottomNav } from './BottomNav/hooks'
import { useSubmitNavigationStyle } from './Style/hooks'

import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { useDispatch, useSelector } from 'react-redux'
import { selectApp } from 'selectors/app'
import { selectThemeId } from 'selectors/landingPageTheme'
import { selectBottomNavHasChanges } from 'slices/Cms/Content/AppNavigation/BottomNav/selectors'
import { selectIsFormValid } from 'slices/Cms/Content/AppNavigation/selectors'
import {
  selectStyle,
  selectStyleHasChanges,
} from 'slices/Cms/Content/AppNavigation/Style/selectors'
import BottomNav from './BottomNav'
import HamburgerNav from './HamburgerNav'
import Style from './Style'

export enum Tabs {
  AppNavigation = 'app_navigation_style',
  Style = 'app_navigation_style',
  HamburgerNav = 'app_navigation_hamburger_nav',
  BottomNav = 'app_navigation_bottom_nav',
}

const t = buildTranslate('cms.content.app_navigation')

// Hook to determine if AppNavigation should be visible at all
// This is NOT coupled to individual tab implementations
export const useAppNavigationTabsVisible = () => {
  // Feature flags for each sub-tab
  const styleEnabled = useFlag('bottom-nav-toggle-mx', false)
  const hamburgerNavEnabled = useFlag('manage-hamburger-nav-mx', false)
  const bottomNavEnabled = useFlag('customise-bottom-nav-mx', false)

  // Could also include other conditions like:
  // - User permissions
  // - App configuration
  // - Business rules
  // - etc.

  // Check if at least one tab feature is enabled
  return styleEnabled || hamburgerNavEnabled || bottomNavEnabled
}

// Focused hooks for individual tabs - each owns its specific logic
export const useStyleTab = (): TabT | null => {
  const styleEnabled = useFlag('bottom-nav-toggle-mx', false)

  if (!styleEnabled) return null

  return {
    key: Tabs.Style,
    title: t('style.tab_title'),
    component: Style,
  }
}

export const useHamburgerTab = (): TabT | null => {
  const hamburgerNavEnabled = useFlag('manage-hamburger-nav-mx', false)

  if (!hamburgerNavEnabled) return null

  return {
    key: Tabs.HamburgerNav,
    title: t('hamburger_nav.tab_title'),
    component: HamburgerNav,
  }
}

export const useBottomTab = (): TabT | null => {
  const bottomNavEnabled = useFlag('customise-bottom-nav-mx', false)
  const bottomPlacementSelected =
    useSelector(selectStyle)?.settings?.placement === 'bottom'

  if (!bottomNavEnabled || !bottomPlacementSelected) return null

  return {
    key: Tabs.BottomNav,
    title: t('bottom_nav.tab_title'),
    component: BottomNav,
  }
}

// Required for legacy tabs compatibility
// - this hook is not used in brand_content/app_navigation/ routes
// - this hook will be used in brand_content#<anything> routes
export const useAppNavigationTabs = () => {
  // Use dedicated visibility hook - clear responsibility separation
  const isVisible = useAppNavigationTabsVisible()

  // Use focused sub-tab hooks - each responsible for their own logic
  const styleTab = useStyleTab()
  const hamburgerNavTab = useHamburgerTab()
  const bottomNavTab = useBottomTab()

  // Check visibility after calling all hooks
  if (!isVisible) {
    return []
  }

  const subtabs: TabT[] = []

  // Collect enabled tabs
  if (styleTab) subtabs.push(styleTab)
  if (hamburgerNavTab) subtabs.push(hamburgerNavTab)
  if (bottomNavTab) subtabs.push(bottomNavTab)

  // Return the main AppNavigation tab structure with link to standalone page
  return [
    {
      key: Tabs.AppNavigation,
      title: t('tab_title'),
      subtabs,
      component: () => null, // Lazy-loaded component to avoid circular dependency
      link: '/brand_content/app_navigation', // Link to standalone AppNavigation page
    },
  ]
}

export const useAppNavigationInit = () => {
  const app = useSelector(selectApp)
  const themeId = useSelector(selectThemeId)

  // Fetch landing page theme - theme ID is now read directly from query cache
  const { data: landingPageThemeData } =
    landingPageThemeApi.useGetLandingPageThemeQuery(app?.id ?? 0, {
      skip: !app?.id,
    })

  // TODO: Add redirect to app home page if theme ID is nil
  // This should redirect to the app home page when theme ID is not available

  return {
    themeId,
    isLoading: !landingPageThemeData && !!app?.id,
  }
}

export const useSelectCanSubmit = () => {
  const styleHasChanges = useSelector(selectStyleHasChanges)
  const bottomNavHasChanges = useSelector(selectBottomNavHasChanges)
  const hasChanges = styleHasChanges || bottomNavHasChanges
  const isStyleSubmitting = useSelector(selectNavigationStyleIsSubmitting)
  const isBottomNavSubmitting = useSelector(selectNavigationTabsIsSubmitting)
  const isFormSubmitting = isStyleSubmitting || isBottomNavSubmitting
  const isFormValid = useSelector(selectIsFormValid)
  const themeId = useSelector(selectThemeId)

  return hasChanges && !isFormSubmitting && isFormValid && !!themeId
}

// Form submission hook
export const useHandleFormSubmit = () => {
  const dispatch = useDispatch()
  const submitNavigationStyle = useSubmitNavigationStyle()
  const submitBottomNav = useSubmitBottomNav()
  const styleHasChanges = useSelector(selectStyleHasChanges)
  const bottomNavHasChanges = useSelector(selectBottomNavHasChanges)
  const t = buildTranslate('cms.content.app_navigation')

  const handleSubmit = async () => {
    try {
      // Collect submission functions only for slices that have changes
      const submissions: Promise<any>[] = []

      if (styleHasChanges) {
        submissions.push(submitNavigationStyle())
      }

      if (bottomNavHasChanges) {
        submissions.push(submitBottomNav())
      }

      // Execute all submissions in parallel
      if (submissions.length > 0) {
        await Promise.all(submissions)
      }

      // Show success notification
      dispatch(
        alert({
          key: 'success',
          message: t('submit_success'),
          timeout: 5,
        })
      )
    } catch (error) {
      // Show error notification
      dispatch(
        alert({
          key: 'danger',
          message: t('submit_error'),
          timeout: 5,
        })
      )
      // Don't re-throw the error to prevent app crashes
      // The error notification is sufficient feedback to the user
    }
  }

  return handleSubmit
}
